import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { v1 as uuid } from 'uuid';
import { RouterService } from './router.service';
import { SessionTimerService } from './session-timer.service';
import { environment } from '../../environments/environment';
import { SessionStorageService } from './session-storage.service';
import { User } from '../types/user';
import { TokenTimerService } from './token-timer.service';
import { Application } from '../types/application';
import { LogoutService } from './logout.service';
import { PasswordPolicyService } from './password-policy.service';
import { Subject } from 'rxjs';
import { AuditService } from './audit.service';
import { MessageService } from './message.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { PrivacyNoticeService } from './privacy.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private isSessionStarted = false;
  user: User;
  logoutEvent = new Subject<any>();

  private defaultApp = environment.applications.home;
  private accessApp = environment.applications.access;
  


  constructor(
    private log: LogService,
    private http: HttpClient,
    private logoutService: LogoutService,
    private passwordPolicy: PasswordPolicyService,
    private router: RouterService,
    private sessionTimer: SessionTimerService,
    private tokenTimer: TokenTimerService,
    private sessionStorage: SessionStorageService,
    private auditService: AuditService,
    private spinner: SpinnerService,
    private privacynoticeService: PrivacyNoticeService,
    private messageService: MessageService
  ) {
    this.sessionTimer.onSessionTerminateEvent(() => {
      this.auditService.auditEvent(
        this.getUser().username,
        'AutomaticLogout',
        'User session was automatically terminated due to inactivity',
        {}
      );

      if (window !== window.parent) {
        this.messageService.postLogoutMessage();
      } else {
        this.router.gotoLogout();
      }
    });

    this.tokenTimer.onSessionTerminateEvent(() => {
      if (window !== window.parent) {
        this.messageService.postLogoutMessage();
      } else {
        this.router.gotoLogout();
      }
    });

    window.addEventListener('message', event => {

      /* Temporaily commenting out for stage
       this.log.trace('SessionService.onWindowEvent', {
         data: event.data,
         origin: event.origin
       });
      */

       if (event.data.type) {
          if (event.data.type!=='uux-keep-alive') {
            this.registerUserAction();
          };

          if (event.data.type=='UUX-Patient-Context') {
            try {
              const patientAry=event.data.patient;
              const patientInfo = {MRN:patientAry[0].patientMrn,AA:patientAry[0].patientOrganization, UUXSessionId: this.sessionStorage.getUUXSessionId(), UserName: this.sessionStorage.getUser().username};
              this.sessionStorage.setUUXPatientContext(JSON.stringify(patientInfo));
              const hsApi = environment.api.setCVPatientContext; 
            
              this.log.trace('Set UUX-Patient-Context: '+JSON.stringify(patientInfo));
              this.http.post(hsApi, patientInfo).subscribe(
                function() {
                  // On success, do nothing
                },
                function(error) {
                  // On error, log the error message
                  this.log.trace('Error while setting patient context:', error);
                }
              );
            }catch(error) {
              this.log.trace('Error while setting patient context:', error);
            }
          }
       }

    });
  }

  getUser(): User {
    return this.user;
  }

  registerUserAction() {
    this.sessionTimer.resetIdleCountdown();
  }

  isAuthenticated(): boolean {
    this.log.trace('SessionService.isAuthenticated', {
      isAuthenticated: this.isSessionStarted
    });
    return this.isSessionStarted;
  }

  callNotifyLogout() {
    const logoutApi = _.get(environment, 'notify.getLogoutRequest');
    if (logoutApi) {
      const apiCall = logoutApi(this.sessionStorage).get.url;
      this.http.get(apiCall).subscribe(_.noop, _.noop);
    }
  }

  start() {
    const idToken = this.sessionStorage.getIdToken();

    this.log.trace('SessionService.start.parameters', {
      isSessionStarted: this.isSessionStarted,
      isTokenValid: idToken.isValid()
    });

    if (!this.isSessionStarted && idToken.isValid()) {
      // reuse existing UUX session ID, if available
      const uuxSessionId = this.sessionStorage.getUUXSessionId() || uuid();
      this.sessionStorage.setUUXSessionId(uuxSessionId);

      // set default app if not set
      let app = this.sessionStorage.getApplication();
      if (!app) {
        const appUrl = this.defaultApp.getUrl();
        app = new Application(
          appUrl.url,
          appUrl.body,
          this.defaultApp.name,
          !!this.defaultApp.topLevelRedirect
        );
        this.sessionStorage.setApplication(app);
      }

      this.user = this.sessionStorage.getUser();
      this.user.initialize();
      this.user.organization = this.sessionStorage.getOrganization();
      this.privacynoticeService.setUser(this.user);
      this.log.trace('SessionService.start.user', this.user);
      this.auditService.setUser(this.user);
      // Pre-fetch password policy
      this.passwordPolicy
        .getPasswordPolicy(
          this.user.organization.UserPoolId,
          this.user.username
        )
        .subscribe();

      this.sessionTimer.start();
      this.tokenTimer.start();

      this.isSessionStarted = true;
    }
  }

  terminate() {
    this.isSessionStarted = false;
    this.sessionTimer.stop();
    this.tokenTimer.stop();
    this.passwordPolicy.clearPasswordPolicy();
    this.user = null;
    this.privacynoticeService.setUser(this.user);
    this.logoutEvent.next();
    this.logoutService.logout(() => {
      const logoutUrl = this.sessionStorage.getLogoutURL();
      window.sessionStorage.clear();
      if (logoutUrl) {
        window.location.href = logoutUrl;
      }
      else {
        this.router.gotoLogin();
        setTimeout(() => window.location.reload());
        this.messageService.postRedirectMessage();
        this.spinner.hideSpinner();
      }
    });
  }
}
